/* @flow */

import type { CombinedReducers } from '../../../../reducers';
import type { Dispatch } from '../../../../types/types';
import type { RequestResponseMethodDefinitionType } from '../../emitter';
import type { BO_RESPONSE_WITH_ETAG } from '../types/common';

import { MILLISECONDS_PER_SECOND } from '../../../../../helpers/dateTime/Format';
import { logError } from '../../../../../helpers/debug/debug';
import { type AllSettledPromises, SettledPromiseFulfilled } from '../../../../../helpers/jsHelpers/promise';
import { CustomNetworkError } from '../../../../../libs/netgemLibrary/helpers/CustomNetworkError';
import { HttpStatus } from '../../../../../libs/netgemLibrary/v8/constants/NetworkCodesAndMessages';
import { updateBOPurchaseListForDistributor, updateBOPurchaseListsForAllDistributors } from '../../../../ui/actions';
import sendPurchaseListRequest from '../purchaseList';
import { type BO_PURCHASE_LIST_BY_DISTRIBUTOR_TYPE, type BO_PURCHASE_LIST_FOR_DISTRIBUTOR, type BO_PURCHASE_LISTS, Purchase } from '../types/purchase';

const getPurchaseList: (signal?: AbortSignal) => RequestResponseMethodDefinitionType =
  (signal) =>
  (dispatch: Dispatch, getState: () => CombinedReducers): Promise<void> => {
    const {
      netgemApi: { distributors },
    } = getState();

    const promises: Array<Promise<BO_PURCHASE_LIST_FOR_DISTRIBUTOR>> = Array.from(Object.keys(distributors)).map((distributorId) =>
      dispatch(getPurchaseListForDistributor(distributorId, signal, true)),
    );

    return Promise.allSettled(promises).then((results: AllSettledPromises<BO_PURCHASE_LIST_FOR_DISTRIBUTOR>) => {
      if (signal?.aborted) {
        return;
      }

      const allPurchaseLists: BO_PURCHASE_LISTS = [];

      results.forEach(({ status, value }) => {
        // When a response is the same as previously (HTTP 304), 'value' is undefined
        if (status === SettledPromiseFulfilled && value !== undefined) {
          allPurchaseLists.push(value);
        }
      });

      dispatch(updateBOPurchaseListsForAllDistributors(allPurchaseLists));
    });
  };

const getPurchaseListForDistributor: (distributorId: string, signal?: AbortSignal, skipStoreUpdate?: boolean) => RequestResponseMethodDefinitionType =
  (distributorId, signal, skipStoreUpdate) =>
  (dispatch: Dispatch): Promise<BO_PURCHASE_LIST_FOR_DISTRIBUTOR> =>
    dispatch(sendPurchaseListRequest(distributorId, signal))
      .then(({ eTag, response }: BO_RESPONSE_WITH_ETAG) => {
        if (signal?.aborted || !response) {
          return Promise.reject(new Error(`Could not get purchase list for ${distributorId}`));
        }

        const purchaseList: BO_PURCHASE_LIST_BY_DISTRIBUTOR_TYPE = {};

        const { body } = response;
        body.forEach((p) => {
          const {
            data: {
              purchaseTitleData: { licenseExpirationDate, licenseTimeLeft, packVtiId, vhiDate, videoPosition, vtiId },
              subType,
            },
          } = p;
          const existingItem = purchaseList[vtiId.toString()];

          if (!existingItem || (existingItem.packVtiId === undefined && packVtiId !== undefined)) {
            purchaseList[vtiId.toString()] = {
              expirationTime: licenseExpirationDate ? new Date(licenseExpirationDate).getTime() / MILLISECONDS_PER_SECOND : 0,
              lastBookmark: videoPosition,
              packVtiId,
              purchaseDate: vhiDate,
              subType,
              type: licenseTimeLeft === undefined || licenseTimeLeft === null ? Purchase.Buy : Purchase.Rent,
            };
          }
        });

        if (!skipStoreUpdate) {
          dispatch(updateBOPurchaseListForDistributor(distributorId, purchaseList, eTag));
        }

        return { distributorId, eTag, purchaseList };
      })
      .catch((error: Error | CustomNetworkError) => {
        if (error instanceof CustomNetworkError && error.getStatus() !== HttpStatus.NotModified) {
          logError(`Error retrieving purchase list for "${distributorId}": ${error.message}`);
        }
      });

export { getPurchaseList, getPurchaseListForDistributor };
